import React, { useState, useEffect } from 'react';
import Icon from '@components/AppIcon';
import Button from '@components/ui/Button';
import ReactMarkdown from 'react-markdown';

const ServiceCategories = () => {
  const [activeCategory, setActiveCategory] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await fetch('/api/our_services');
        if (!response.ok) {
          throw new Error('Failed to fetch services');
        }
        const data = await response.json();
        setServices(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, []);

  const activeService = services.find(cat => cat.id === activeCategory);

  // Map service names to icons
  const iconMap = {
    'General Eye Check-Up': 'Search',
    'Cataract Surgery': 'Eye',
    'LASIK & Refractive Surgery': 'Zap',
    'Glaucoma Management': 'EyeOff',
    'Retina Care': 'Activity',
    'Cornea Care': 'Circle',
    'Keratoconus Treatment': 'Triangle',
    'Squint (Strabismus) Treatment': 'Target',
    'Refractive Error Correction': 'Glasses',
    'Optical Services': 'ShoppingBag'
  };

  if (loading) {
    return (
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 lg:px-6 text-center">
          <p>Loading services...</p>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 lg:px-6 text-center">
          <p>Error loading services: {error}</p>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 lg:px-6">
        <div className="text-center mb-16">
          <h2 className="font-heading font-bold text-3xl lg:text-4xl text-[#08005f] mb-4">
            Our Specialized Services
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            From preventive care to advanced surgeries, our specialized services cover every stage of eye health, delivered with precision, compassion, and world-class expertise.
          </p>
        </div>

        {/* Card Grid Layout */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service) => (
            <div
              key={service.id}
              className="bg-white rounded-2xl shadow hover:shadow-lg overflow-hidden transition"
            >
              <img
                src={service.image}
                alt={service.name}
                className="w-full h-40 object-cover"
                onError={(e) => {
                  e.target.src = 'https://via.placeholder.com/400x200?text=Service+Image';
                }}
              />
              <div className="p-6">
                <div className="flex items-center space-x-3 mb-3">
                  <Icon 
                    name={iconMap[service.name] || 'Circle'} 
                    size={22} 
                    color="#eb2227" 
                  />
                  <h3 className="font-heading font-semibold text-xl text-[#08005f]">
                    {service.name}
                  </h3>
                </div>
                <p className="text-text-secondary text-sm mb-4 line-clamp-3">
                  {service.description}
                </p>
                <Button
                  variant="outline"
                  className="border-[#08005f] text-[#08005f] hover:bg-[#eb2227] hover:text-white"
                  iconName="FileText"
                  iconSize={14}
                  onClick={() => {
                    setActiveCategory(service.id);
                    setShowModal(true);
                  }}
                >
                  Learn More
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Modal */}
      {showModal && activeService && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-8 relative animate-fade-in overflow-y-auto max-h-screen">
            <button
              className="absolute top-4 right-4 text-gray-400 hover:text-[#08005f]"
              onClick={() => setShowModal(false)}
              aria-label="Close"
            >
              <Icon name="X" size={24} />
            </button>
            <div className="flex items-center space-x-4 mb-6">
              <div className="w-14 h-14 rounded-xl flex items-center justify-center bg-[#08005f]">
                <Icon 
                  name={iconMap[activeService.name] || 'Circle'} 
                  size={28} 
                  color="white" 
                  strokeWidth={2} 
                />
              </div>
              <div>
                <h3 className="font-heading font-bold text-2xl text-[#08005f]">
                  {activeService.name}
                </h3>
                <p className="text-text-secondary">{activeService.description}</p>
              </div>
            </div>
            <div className="prose max-w-none">
              <ReactMarkdown>
                {activeService.service_description}
              </ReactMarkdown>
            </div>
          </div>
        </div>
      )}

      {/* Modal animation */}
      <style>
        {`
          .animate-fade-in {
            animation: fadeInModal 0.25s ease;
          }
          @keyframes fadeInModal {
            from { opacity: 0; transform: translateY(20px);}
            to { opacity: 1; transform: translateY(0);}
          }
        `}
      </style>
    </section>
  );
};

export default ServiceCategories;